import { ThemeProvider } from '@mui/system';
import cx from 'classnames';
import type { Asset, Entry } from 'src/types/contentful-sdk-compat';
import {
  forwardRef,
  type MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type FC,
} from 'react';
import { useMainTemplateContext } from 'src/contexts/MainTemplateContext/MainTemplateContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import {
  StyledContainer,
  StyledCta,
  StyledCtaWrapper,
  StyledFlexCol,
  StyledLink,
  StyledLinkNumber,
  StyledLinks,
  StyledLinkSeparator,
  StyledLinkText,
  StyledLogoAnchor,
  StyledLogoWrapper,
  StyledNav,
  StyledOverflow,
  StyledOverflowIndicators,
  StyledPlaidLogo,
  StyledPlaidLogoWaffle,
  StyledProgressBar,
  StyledProgressBarShape,
  StyledSubnav,
  StyledSubnavPlaceholder,
} from './Subnav.styled';

type TSubNavBackgroundImage = Asset;

type TSubNavLink = {
  internalTitle: string;
  text: string;
  target: string;
  url: string;
};

type TSubNav = {
  internalTitle: string;
  links: Array<Entry<TSubNavLink>>;
  /**
   * Whether to number the links on large screens. Entries that predate the
   * field have no value and must keep their numbers, so only an explicit
   * `false` hides them.
   */
  showNumbers?: boolean;
  progressBarImage: TSubNavBackgroundImage;
  ctaLink?: Entry<TSubNavLink>;
};

type TAnchorId = string;

const Overflow = forwardRef<HTMLDivElement, { children: ReactNode }>(
  ({ children }, ref) => {
    const innerRef = useRef<HTMLDivElement>();

    useImperativeHandle(ref, () => {
      return innerRef.current;
    });

    const [hasLeftOverflow, setHasLeftOverflow] = useState(false);
    const [hasRightOverflow, setHasRightOverflow] = useState(false);

    useEffect(() => {
      const innerEl = innerRef.current;
      if (!innerRef.current) {
        return;
      }

      const handleScroll = () => {
        const scrollLeft = innerEl.scrollLeft;
        const maxOverflowWidth = innerEl.scrollWidth - innerEl.offsetWidth;
        const hasLeftOverflow = scrollLeft > 0;
        // `Math.ceil` as `scrollLeft` can be a decimal causing us to never hit `maxOverflowWidth` exactly
        const hasRightOverflow = Math.ceil(scrollLeft) < maxOverflowWidth;

        setHasLeftOverflow(hasLeftOverflow);
        setHasRightOverflow(hasRightOverflow);
      };

      handleScroll();

      innerEl.addEventListener('scroll', handleScroll);

      const resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(innerEl);

      return () => {
        innerEl.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }, []);

    return (
      <StyledOverflowIndicators
        hasLeftOverflow={hasLeftOverflow}
        hasRightOverflow={hasRightOverflow}
      >
        <StyledOverflow ref={innerRef}>{children}</StyledOverflow>
      </StyledOverflowIndicators>
    );
  },
);

const Link = forwardRef<
  HTMLAnchorElement,
  {
    children: ReactNode;
    isActive: boolean;
    index: number;
    showNumber: boolean;
  } & Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>
>(({ children, isActive, index, showNumber, ...props }, ref) => {
  return (
    <StyledLink isActive={isActive}>
      <a ref={ref} {...props}>
        {/* Not rendered at all when hidden, so the number and separator
            leave no gap or empty flex items behind. */}
        {showNumber && (
          <>
            <StyledLinkNumber isActive={isActive} aria-hidden='true'>
              {(index + 1).toString().padStart(2, '0')}
            </StyledLinkNumber>
            <StyledLinkSeparator />
          </>
        )}
        <StyledLinkText isActive={isActive}>{children}</StyledLinkText>
      </a>
    </StyledLink>
  );
});

const ProgressBar: FC<{
  backgroundImageUrl: string;
  width: number;
}> = ({ backgroundImageUrl, width }) => {
  const ref = useRef<HTMLDivElement>();

  const progressBarWidth = ref.current?.offsetWidth ?? 1;
  const percent = Math.min(1, width / progressBarWidth);
  const isFullWidth = percent >= 1;

  return (
    <StyledProgressBar ref={ref}>
      <StyledProgressBarShape
        style={{
          // `clipPath` is required to clip a progress bar whose max-width is not
          // always `100vw` while keeping underlying image dimensions consistent.
          clipPath: `inset(0 ${(1 - percent) * 100}% 0 0${
            // Remove right-hand corner radius when full-width
            !isFullWidth ? ' round 0 8px 8px 0' : ''
          })`,
        }}
      >
        <img
          // We expect some form of a gradient image here that matches themes of sections on the page.
          // The gradient should be consistent top-to-bottom, so we only need 1px and can stretch.
          src={`${backgroundImageUrl}?fm=webp&w=1920&h=1&fit=scale`}
          alt=''
        />
      </StyledProgressBarShape>
    </StyledProgressBar>
  );
};

/**
 * Dynamically offsets anchors such that `example.org#anchor` lines
 * up exactly with subnav height and triggers active logic.
 */
const AnchorStylesheet: FC<{ hashes: string[]; subnavHeight: number }> = ({
  hashes,
  subnavHeight,
}) => {
  return (
    <style>
      {hashes
        .map(
          // -1 to account for subpixel scrolling and making sure we're
          // "far enough" down the page to trigger subnav active logic.
          (hash) => {
            return `${hash}:target{scroll-margin-top: ${Math.max(
              0,
              subnavHeight - 1,
            )}px;}`;
          },
        )
        // Interestingly, `join('')` is required within the `<style>` tag to prevent a
        // React hydration error stemming from individual text nodes. This
        // does not impact a text-based tag, like a `<div>`.
        .join('')}
    </style>
  );
};

/**
 * The subnav component sits below a hero section on a given page and
 * is intended to navigate between various anchors on the page.
 */
const Subnav: FC<Entry<TSubNav>> = ({ fields }) => {
  const { setIsNavigationHidden, setIsForeignContentNotificationHidden } =
    useMainTemplateContext();
  const componentTheme = useComponentTheme('light');

  const showNumbers = fields.showNumbers !== false;

  const [windowScrollY, setWindowScrollY] = useState(0);
  const [sortedAnchorBounds, setSortedAnchorBounds] = useState<
    Array<{
      anchor: Element;
      anchorId: TAnchorId;
      anchorTop: number;
      anchorBottom: number;
    }>
  >([]);
  const [progressBounds, setProgressBounds] = useState<
    Record<
      TAnchorId,
      {
        progressLeft: number;
        progressRight: number;
        progress: Element;
      }
    >
  >({});

  /**
   * Determines if subnav is stickied to the top of the page. We can't use `position: sticky`
   * because parent elements have `overflow: hidden` and life is unfair.
   */
  const [isFixedToPage, setIsFixedToPage] = useState(false);

  const [subnavHeight, setSubnavHeight] = useState(0);

  const linkRefs = useRef<Record<TAnchorId, HTMLAnchorElement>>({});
  const logoRef = useRef<HTMLDivElement>();
  const overflowRef = useRef<HTMLDivElement>();
  const overflowRefTargetScrollLeft = useRef(0);
  const placeholderRef = useRef<HTMLDivElement>();
  const subnavContentRef = useRef<HTMLDivElement>();
  const subnavRef = useRef<HTMLDivElement>();

  /** List of unsorted page anchors and corresponding links in subnav */
  const config: Array<{
    id: TAnchorId;
    anchorSelector: () => Element;
    progressSelector: () => Element;
  }> = useMemo(() => {
    return [
      {
        id: '___internal-subnav-above-the-fold',
        anchorSelector: () => {
          return document.body;
        },
        progressSelector: () => {
          return logoRef.current;
        },
      },
      ...fields.links.map(({ fields: { url: hash }, sys: { id } }) => {
        return {
          id,
          anchorSelector: () => {
            return document.querySelector(hash);
          },
          progressSelector: () => {
            return linkRefs.current[id];
          },
        };
      }),
    ];
  }, [fields.links]);

  /** List of #anchors that are targeted by links in the subnav */
  const targetablePageAnchors = [
    ...fields.links.map(({ fields: { url } }) => {
      return url;
    }),
    ...(fields.ctaLink?.fields.url ? [fields.ctaLink.fields.url] : []),
  ].filter((anchor) => {
    return anchor.startsWith('#');
  });

  /** Determines bounds of page anchors and their corresponding links in the nav bar */
  const calculateSortedAnchorBounds = useCallback(() => {
    const placeholderEl = placeholderRef.current;
    if (!placeholderEl) {
      return;
    }

    const windowScrollY = window.scrollY;
    const placeholderRect = placeholderEl.getBoundingClientRect();

    // Offset we want to subtract from all `getBoundingClientRect().top` calls
    // to get the correct position on the page regardless of scroll position.
    const rectTopOffset = placeholderRect.height - windowScrollY;

    // Section length follows page order, not the order the links were authored
    // in. Fall 2026 lists Fraud before Money movement even though Fraud is
    // lower on the page; using that authored neighbor made Fraud's range run
    // upward, so the indicator stalled on Fraud and reversed when scrolling up.
    const measured = config
      .map(({ id: anchorId, anchorSelector }) => {
        const anchor = anchorSelector();
        if (!anchor) {
          return null;
        }

        return {
          anchorId,
          anchor,
          anchorTop: Math.max(
            anchor.getBoundingClientRect().top - rectTopOffset,
            0,
          ),
        };
      })
      .filter(
        (
          bounds,
        ): bounds is {
          anchorId: TAnchorId;
          anchor: Element;
          anchorTop: number;
        } => {
          return bounds != null;
        },
      )
      .sort((a, b) => {
        return a.anchorTop - b.anchorTop;
      });

    setSortedAnchorBounds(
      measured.map((bounds, index) => {
        // Above the fold is the space between the top of the page and the
        // bottom of the placeholder, not the distance to the first anchor.
        if (bounds.anchorId === '___internal-subnav-above-the-fold') {
          return {
            ...bounds,
            anchorBottom: placeholderRect.bottom - rectTopOffset,
          };
        }

        const next = measured[index + 1];
        if (next) {
          return { ...bounds, anchorBottom: next.anchorTop };
        }

        // Progress is measured as the distance scrolled between anchors. When
        // working with the last anchor, fall back to the top of the page CTA.
        const ctaUrl = fields.ctaLink?.fields.url;
        const ctaAnchor =
          ctaUrl?.startsWith('#') && document.querySelector(ctaUrl);

        return {
          ...bounds,
          anchorBottom: ctaAnchor
            ? Math.max(ctaAnchor.getBoundingClientRect().top - rectTopOffset, 0)
            : document.body.scrollHeight - window.innerHeight,
        };
      }),
    );
  }, [config, fields.ctaLink, placeholderRef]);

  const sortedLinks = sortedAnchorBounds
    .map(({ anchorId: containerId }) => {
      return fields.links.find(({ sys: { id } }) => {
        return id === containerId;
      });
    })
    .filter(Boolean);

  /** Find all sections user has scrolled past */
  const scrolledPastBounds = sortedAnchorBounds.filter(
    // Note: this does not catch sections which cannot be scrolled
    // past due to being near the bottom of the page.
    ({ anchorTop: containerTop }) => {
      return containerTop <= windowScrollY;
    },
  );

  /** Used by anchors to progressively highlight */
  const scrollPastBoundIds = scrolledPastBounds.map(
    ({ anchorId: containerId }) => {
      return containerId;
    },
  );

  /** Last section user has scrolled past */
  const matchingBounds = scrolledPastBounds[scrolledPastBounds.length - 1];
  const matchingProgressBounds = progressBounds[matchingBounds?.anchorId];

  /** Calculates progress bar width relative to anchors in subnav */
  const progressWidth = (() => {
    if (!matchingBounds || !matchingProgressBounds) {
      return 0;
    }

    const matchingBoundsIndex = sortedAnchorBounds.findIndex(
      ({ anchorId: containerId }) => {
        return containerId === matchingBounds.anchorId;
      },
    );

    const nextMatchingBounds = sortedAnchorBounds[matchingBoundsIndex + 1];
    const nextMatchingProgressBounds =
      progressBounds[nextMatchingBounds?.anchorId];

    const percentThroughSection = Math.min(
      1,
      Math.max(
        0,
        (windowScrollY - matchingBounds.anchorTop) /
          (matchingBounds.anchorBottom - matchingBounds.anchorTop),
      ),
    );

    return (
      matchingProgressBounds.progressLeft +
      ((nextMatchingProgressBounds?.progressLeft ??
        matchingProgressBounds.progressRight) -
        matchingProgressBounds.progressLeft) *
        percentThroughSection
    );
  })();

  useLayoutEffect(() => {
    // The browser may already be at a hash. Match that before paint so the
    // indicator doesn't draw at the top and then jump.
    setWindowScrollY(window.scrollY);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleScroll = () => {
      setWindowScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      return window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  /** Calculate progress bar bounds */
  useEffect(() => {
    if (!overflowRef.current || !subnavContentRef.current) {
      return;
    }

    const configByContainerId = config.reduce<
      Record<TAnchorId, (typeof config)[number]>
    >((acc, configEntry) => {
      acc[configEntry.id] = configEntry;
      return acc;
    }, {});

    const containerCount = sortedAnchorBounds.length;
    const navigationScrollX = overflowRef.current.scrollLeft;
    const subnavContentEl = subnavContentRef.current;

    setProgressBounds(
      sortedAnchorBounds
        .map(({ anchorId: containerId }, index) => {
          const configEntry = configByContainerId[containerId];
          // Guard against race where `sortedAnchorBounds` contains IDs that are
          // no longer present in `config` when this effect re-runs early.
          if (!configEntry) {
            return null;
          }

          const progress = configEntry.progressSelector();

          // This should technically never happen at this point as
          // links are generated from `sortedContainerBounds`.
          if (!progress) {
            return null;
          }

          let { left: progressLeft, right: progressRight } =
            progress.getBoundingClientRect();

          if (index === 0) {
            progressLeft = subnavContentEl.getBoundingClientRect().left;
          } else {
            if (index === containerCount - 1) {
              progressRight = subnavContentEl.getBoundingClientRect().right;
            }
          }

          return {
            containerId,
            progressLeft: Math.max(progressLeft + navigationScrollX, 0),
            progressRight: progressRight + navigationScrollX,
            progress,
          };
        })
        .filter(Boolean)
        .reduce<typeof progressBounds>((acc, { containerId, ...rest }) => {
          acc[containerId] = rest;
          return acc;
        }, {}),
    );
    // `showNumbers` changes each link's width, which is what gets measured. It
    // is static on a live page but can flip in live preview, and no resize
    // observer fires when the surrounding columns keep their size.
  }, [config, showNumbers, sortedAnchorBounds]);

  useEffect(() => {
    const logoEl = logoRef.current;
    if (!logoEl) {
      return;
    }

    calculateSortedAnchorBounds();

    const resizeObserver = new ResizeObserver(calculateSortedAnchorBounds);

    resizeObserver.observe(document.body);
    resizeObserver.observe(
      logoEl,
      { box: 'border-box' }, // Observe padding change
    );

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateSortedAnchorBounds]);

  useEffect(() => {
    const placeholderEl = placeholderRef.current;
    if (!placeholderEl) {
      return;
    }

    const handleResize = () => {
      setSubnavHeight(placeholderEl.offsetHeight);
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(placeholderEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    setIsFixedToPage(
      sortedAnchorBounds.length > 0 &&
        windowScrollY >= sortedAnchorBounds[0].anchorBottom,
    );
  }, [sortedAnchorBounds, windowScrollY]);

  /**
   * Native hash navigation jumps. Scroll ourselves so clicks and a hash
   * already in the URL animate, stopping one pixel short of the bar the way
   * the `:target` offset does. Reduced motion skips the animation.
   */
  const scrollToHash = useCallback((hash: string) => {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)')
      .matches
      ? 'auto'
      : 'smooth';

    if (!hash || hash === '#') {
      window.scrollTo({ top: 0, behavior });
      return true;
    }

    let id: string;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return false;
    }

    const target = document.getElementById(id);
    if (!target) {
      return false;
    }

    const offset = Math.max(0, (placeholderRef.current?.offsetHeight ?? 0) - 1);
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top, behavior });
    return true;
  }, []);

  const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const href = event.currentTarget.getAttribute('href');
    if (!href?.startsWith('#')) {
      return;
    }

    if (!scrollToHash(href)) {
      return;
    }

    event.preventDefault();

    const nextHash = href === '#' ? '' : href;
    if (window.location.hash !== nextHash) {
      window.history.pushState(
        null,
        '',
        nextHash || `${window.location.pathname}${window.location.search}`,
      );
    }
  };

  // A hash already in the URL has been jumped to by the browser. Ease to the
  // same spot under the bar instead of replaying the scroll from the top,
  // which runs the indicator backward.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash === '#') {
      return;
    }

    scrollToHash(hash);
  }, [scrollToHash]);

  /** Hide main page navigation when subnav becomes sticky */
  useEffect(() => {
    setIsNavigationHidden(isFixedToPage);
    setIsForeignContentNotificationHidden(isFixedToPage);
  }, [
    isFixedToPage,
    setIsForeignContentNotificationHidden,
    setIsNavigationHidden,
  ]);

  /** Keep target anchor centered in overflow on mobile */
  if (overflowRef.current && matchingProgressBounds) {
    const overflowEl = overflowRef.current;

    const targetScrollLeft = Math.max(
      0,
      matchingProgressBounds.progressLeft -
        overflowEl.offsetWidth / 2 +
        (matchingProgressBounds.progressRight -
          matchingProgressBounds.progressLeft) /
          2,
    );

    // Repeatedly calling `scrollTo` on iOS Safari causes a lagging animation
    if (targetScrollLeft !== overflowRefTargetScrollLeft.current) {
      overflowRefTargetScrollLeft.current = targetScrollLeft;

      overflowEl.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    }
  }

  return (
    <>
      <ThemeProvider theme={componentTheme}>
        <StyledSubnavPlaceholder ref={placeholderRef}>
          <StyledSubnav
            isFixed={isFixedToPage}
            ref={subnavRef}
            className={cx('cms-subnav', {
              'cms-subnav--fixed': isFixedToPage,
            })}
          >
            <Overflow ref={overflowRef}>
              <StyledFlexCol ref={subnavContentRef}>
                <StyledContainer>
                  <StyledNav>
                    <StyledLogoWrapper
                      isParentFixed={isFixedToPage}
                      ref={logoRef}
                    >
                      <StyledLogoAnchor
                        href='#'
                        title='Back to top'
                        onClick={handleAnchorClick}
                      >
                        <StyledPlaidLogo />
                        <StyledPlaidLogoWaffle />
                      </StyledLogoAnchor>
                    </StyledLogoWrapper>

                    <StyledLinks>
                      {sortedLinks.map(
                        ({ fields: { url, text }, sys: { id } }, index) => {
                          return (
                            <Link
                              key={
                                `${id}-${index}` /** IDs can technically repeat */
                              }
                              ref={(el) => {
                                linkRefs.current[id] = el;
                              }}
                              isActive={scrollPastBoundIds.includes(id)}
                              index={index}
                              showNumber={showNumbers}
                              href={url}
                              onClick={handleAnchorClick}
                            >
                              {text}
                            </Link>
                          );
                        },
                      )}
                    </StyledLinks>

                    {fields.ctaLink && (
                      <StyledCtaWrapper isParentFixed={isFixedToPage}>
                        <StyledCta
                          href={fields.ctaLink.fields.url}
                          onClick={handleAnchorClick}
                        >
                          {fields.ctaLink.fields.text}
                        </StyledCta>
                      </StyledCtaWrapper>
                    )}
                  </StyledNav>
                </StyledContainer>

                <ProgressBar
                  backgroundImageUrl={fields.progressBarImage.fields.file.url}
                  width={progressWidth}
                />
              </StyledFlexCol>
            </Overflow>
          </StyledSubnav>
        </StyledSubnavPlaceholder>
      </ThemeProvider>

      <AnchorStylesheet
        hashes={targetablePageAnchors}
        subnavHeight={subnavHeight}
      />
    </>
  );
};

export default Subnav;
